import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { map, Observable, of, switchMap, takeWhile, tap, timer } from "rxjs";
import { NotebookDetailsResponse } from "../interfaces/notebook-details-response";
import { mapNotebookDetailsToDisplayed } from "../utility-functions/notebook-utility";
import { NotebookDisplayed } from "../interfaces/notebook-displayed";
import { NotebookCreateRequest } from "../interfaces/notebook-create-request";
import { NOTEBOOK_DEFAULTS } from "../config/notebook-config";
import { AuthService } from "../../auth/services/auth.service";
import { environment } from "../../environments/environment";
import { NOTEBOOK_TARGET_STATUSES, USE_PROXY } from "../config/notebook-config";
import { NotebookEnvironment } from "../interfaces/notebook-environment";

@Injectable({
  providedIn: "root",
})
export class NotebookService {
  private targetUrl = "/v1/notebook";
  private targetUrl2 = "/notebook";
  private capabilitiesPath = "/v1/capabilities/notebook/framework";  // proxy 
  private capabilitiesPath2 = "/capabilities/notebook/framework";    // non-proxy 
  readonly TARGET_STATUSES = NOTEBOOK_TARGET_STATUSES;
  readonly useProxy = USE_PROXY;

  constructor(private http: HttpClient, private authService: AuthService) {}

  private buildUrl(path: string = ""): string {
    if (this.useProxy) {
      // proxy active: relative path, browser will route via dev server
      return `${this.targetUrl}${path}`;
    } else {
      // no proxy:  full URL
      return `${environment.baseUrl}${this.targetUrl2}${path}`;
    }
  }



  private buildCapabilitiesUrl(): string {
    if (this.useProxy) {
      // /v1/capabilities/notebook/framework
      return this.capabilitiesPath;
    } else {
      // base url+ /capabilities/notebook/framework
      return `${environment.baseUrl}${this.capabilitiesPath2}`;
    }
  }


private isQuantumFramework(item: any): boolean {
  return (item?.type ?? "").toString().toLowerCase() === "quantum";
}

//returns frameworks filtered by type quantum
getNotebookEnvironments(): Observable<NotebookEnvironment[]> {
  const base = this.buildCapabilitiesUrl();

  return this.http.get<any>(base).pipe(
    // map + filter client-side
    map((res) => {
      const arr: any[] =
        Array.isArray(res) ? res :
        Array.isArray(res?.items) ? res.items :
        Array.isArray(res?.frameworks) ? res.frameworks : [];

      const onlyQuantum = arr.filter((it) => this.isQuantumFramework(it));
      return onlyQuantum.map((it) => this.mapFrameworkToEnvironment(it));
    }),
  
  );
}


private mapFrameworkToEnvironment(item: any): NotebookEnvironment {
  return {
    editorId: item.editorId ?? "jupyterlab",
    frameworkId: item.id ?? item.frameworkId ??  '',
    frameworkIdLabel:
      item.name ??
      item.frameworkIdLabel ??
      item.label ?? 
      'Unknown',
    frameworkVersions: item.versions ?? item.frameworkVersions ??  [],
     description: item.description ?? '',
    docUrl: item.docUrl ?? '',
    logoUrl: item.logoUrl ?? '',
    savedPaths: item.savedPaths ?? []
  };
}

  //fetch from api, filter by label (user id) and map
  getAllNotebooks(): Observable<NotebookDisplayed[]> {
    const url = this.buildUrl();

    return this.http
      .get<{ items: Partial<NotebookDetailsResponse>[] }>(url)
      .pipe(
        tap({
          //  next: (res) => console.log('Notebook list:', res),
          error: (err) => console.error("Notebook fetch error:", err),
        }),
        map((response) => 
       {
      return this.filterNotebooksByLabel(
            (response.items ?? []).map(mapNotebookDetailsToDisplayed)
      );
    }
    )
      );
  }

  getNotebookById(id: string): Observable<NotebookDisplayed> {
    const url = this.buildUrl(`/${id}`);

    return this.http.get<any>(url).pipe(
      //  tap(response => console.log('notebook api response:', response)),
      map((response) => {
        if (!response || typeof response !== "object") {
          throw new Error("Invalid notebook response structure");
        }
        return mapNotebookDetailsToDisplayed(response);
      })
    );
  }

  getNotebookNameById(id: string): Observable<string> {
    return this.getNotebookById(id).pipe(
      map((notebook: NotebookDisplayed) => notebook.name)
    );
  }

  createNotebook(payload: NotebookCreateRequest): Observable<any> {
    const url = this.buildUrl();
    return this.http.post(url, payload);
  }

  stopNotebook(id: string): Observable<any> {
    const url = this.buildUrl(`/${id}/stop`);
    return this.http.put(url, {});
  }

  startNotebook(id: string): Observable<any> {
    const url = this.buildUrl(`/${id}/start`);
    return this.http.put(url, {});
  }

  deleteNotebook(id: string): Observable<any> {
    const url = this.buildUrl(`/${id}`);
    return this.http.delete(url);
  }

  private filterNotebooksByLabel(
    notebooks: NotebookDisplayed[]
  ): NotebookDisplayed[] {
    const userId = this.authService.getUserId();
    if (!userId) {
      console.warn("No user id found, returning empty notebook list");
      return [];
    }

    return notebooks.filter(
      (nb) => nb.labels && nb.labels[NOTEBOOK_DEFAULTS.labelKey] === userId
    );
  }
}
